import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatRow, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbar } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { defaultUser, User } from 'src/app/interfaces/api/user';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { OrderByNamePipe } from "../../../../pipes/orderByName.pipe";

@Component({
    selector: 'app-registry',
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatPaginator,
        TranslateModule,
        MatToolbar,
        MatCheckboxModule,
        MatTableModule,
        MatRow,
        MatSort,
        ButtonComponent,
        SearchBarComponent,
        MatIconModule,
        SpinnerComponent,
        OrderByNamePipe,
        NoDataComponent,
        ChipComponent
    ],
    standalone: true,
    templateUrl: './registry.component.html',
    styleUrl: './registry.component.scss'
})

export class RegistryComponent implements AfterViewInit, OnDestroy {
    @ViewChild('userPaginator', { static: false }) userPaginator: MatPaginator = new MatPaginator();
    @ViewChild(MatSort) sort: MatSort = new MatSort();
    users: User[] = [];
    usersCount: number = 0;
    usersData: MatTableDataSource<User> = new MatTableDataSource();
    usersColumns: string[] = ['email', 'company', 'project', 'actions'];
    searchText: string = '';
    destroy$ = new Subject<void>();
    loading: boolean = false;
    user: User = defaultUser;
    requestCancel$: Subject<void> = new Subject<void>();
    searchSubject$ = new Subject<string>();
    lastEmptySearch: string = "";

    constructor(
        private auth: AuthService,
        private userService: UserService,
        private translate: TranslateService,
        public router: Router,
        public dialog: MatDialog,
        private pop: Pop
    ) { }

    ngAfterViewInit(): void {
        this.user = this.auth.currentUser();

        this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(text => {
            if (text !== this.searchText) {
                if (this.lastEmptySearch && text.startsWith(this.lastEmptySearch)) return;
                this.searchText = text;
                this.userPaginator.pageIndex = 0;
                this.loadUsers();
            }
        });

        Promise.resolve().then(() => {
            this.loadUsers();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    search(text: string = "") {
        this.searchSubject$.next(text);
    }

    loadUsers() {
        this.requestCancel$.next();
        this.loading = true;

        const query: any = {
            offset: this.userPaginator ? this.userPaginator.pageIndex * this.userPaginator.pageSize : 0,
            limit: this.userPaginator ? this.userPaginator.pageSize : 10,
        };

        const searchText = this.searchText && this.searchText.trim() !== '' ? this.searchText.trim() : "";

        this.userService.searchAndCountUsers(searchText, query)
            .pipe(takeUntil(this.requestCancel$))
            .subscribe({
                next: (res: { results: User[], total: number }) => {
                    this.lastEmptySearch = (res.total === 0 && searchText) ? searchText : "";
                    this.users = res.results.sort((a, b) =>
                        a.email.localeCompare(b.email)
                    );

                    this.usersData.data = this.users;
                    this.usersCount = res.total;

                    if (this.userPaginator)
                        this.userPaginator.length = res.total;

                    this.usersData.sort = this.sort;
                    this.loading = false;
                },
                error: (err) => {
                    console.error(err);
                    this.loading = false;
                }
            });
    }

    editUser(userId: string) {
        this.router.navigate([`users/edit-user`], { queryParams: { userId } })
    }

    deleteUser(user: { id: string, email: string }) {
        const dialogRef = this.dialog.open(DeleteModalComponent, { data: { title: this.translate.instant('users.delete_confirm'), name: user.email } });

        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                this.userService.removeUser(user.id).subscribe({
                    next: () => {
                        this.pop.toast('users.delete_success', 'success', 4000, 'delete');
                        this.loadUsers();
                    },
                    error: (err) => {
                        console.error(err);
                        this.pop.error('users.delete_error');
                    }
                })
            }
        })
    }

    canEdit() {
        if (this.user.role?.name == "dev" || this.user.role?.name == "admin" || this.user.role?.name == "userAdmin") return true;
        return false;
    }

    handlePageEvent(e: PageEvent) {
        this.loadUsers();
    }
}
